// ════════════════════════════════════════════════════════════════════════
//  Einen Lehrgang anlegen – leer oder als Kopie
//
//  Die Kopie ist der übliche Weg: ein zweiter Lehrgang übernimmt Tastatur,
//  Finger und Sternschwellen aus dem ersten und ändert nur die Lektionen.
//
//  Sie vergibt dabei ALLE SCHLÜSSEL NEU. Das ist keine Nebensache: `progress`
//  hängt an (lesson_key, stage_key) ohne jeden Lehrgangsbezug, und zwei
//  Lehrgänge mit denselben Stufenschlüsseln teilen sich still den Fortschritt
//  der Kinder.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseBundle } from "@otto/lehrgang";
import { useSession } from "@otto/plattform";
import { coursePath } from "../../../shared/lib/coursePath.js";
import { keysOf } from "../../../shared/lib/contentKeys.js";
import { useVersions } from "../../versions/lib/data.js";
import {
  SLUG_SHAPE,
  builtInBundle,
  canCreate,
  copyBundle,
  emptyBundle,
  offersBuiltIn,
  type Template,
} from "../lib/newCurriculum.js";
import { useCreateCurriculum, useCurricula } from "../lib/data.js";
import styles from "../../../shared/components/shell.module.css";

/** Wert der Vorlagenauswahl für den eingebauten Lehrgang. Kein Slug, kein Konflikt. */
const BUILT_IN = "__eingebaut__";

export function NewCourseForm() {
  const { client } = useSession();
  const navigate = useNavigate();
  const curricula = useCurricula(client);
  const create = useCreateCurriculum(client);

  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [requires, setRequires] = useState("");
  const [copyFrom, setCopyFrom] = useState("");

  const all = curricula.data ?? [];
  const builtInAvailable = offersBuiltIn(all);
  const source = all.find((c) => c.slug === copyFrom) ?? null;
  // Die Fassungen des Vorlagen-Lehrgangs. Nur dafür wird ein Bundle geladen –
  // die Liste selbst kommt ohne aus.
  const sourceVersions = useVersions(client, source?.slug ?? "");
  const sourceBundle = (() => {
    const latest = (sourceVersions.data ?? []).find((v) => v.publishedAt !== null);
    if (!latest) return null;
    const checked = parseBundle(latest.content);
    return "bundle" in checked ? checked.bundle : null;
  })();

  const template: Template =
    copyFrom === ""
      ? { kind: "empty" }
      : copyFrom === BUILT_IN
        ? { kind: "builtIn" }
        : { kind: "copy", bundleLoaded: sourceBundle !== null };

  const slugOk = SLUG_SHAPE.test(slug) && !all.some((c) => c.slug === slug);
  const ready = canCreate({
    slug,
    title,
    takenSlugs: all.map((c) => c.slug),
    template,
  });

  if (!open) {
    return (
      <div className={`${styles.card} ${styles.spaced}`}>
        <button type="button" className={styles.button} onClick={() => setOpen(true)}>
          Neuen Lehrgang anlegen
        </button>
      </div>
    );
  }

  return (
    <form
      className={`${styles.card} ${styles.spaced}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;

        // Alle vergebenen Schlüssel, über ALLE Lehrgänge – die Kopie darf
        // keinen davon erben.
        const taken = new Set<string>();
        if (sourceBundle) for (const key of keysOf(sourceBundle)) taken.add(key);

        const bundle =
          template.kind === "builtIn"
            ? // Ohne Neu-Verschlüsselung, mit Absicht: der eingebaute Lehrgang
              // steht nur zur Wahl, solange nichts veröffentlicht ist. Seine
              // Schlüssel sollen überall dieselbe Stufe meinen.
              builtInBundle(slug, title.trim())
            : sourceBundle
              ? copyBundle(sourceBundle, slug, title.trim(), taken)
              : emptyBundle(slug, title.trim());

        create.mutate(
          {
            slug,
            title: title.trim(),
            subtitle: subtitle.trim() === "" ? null : subtitle.trim(),
            requiresId: requires === "" ? null : requires,
            position: all.length + 1,
            bundle,
          },
          { onSuccess: () => navigate(coursePath(slug, "uebersicht")) },
        );
      }}
    >
      <h2 className={styles.title} style={{ fontSize: "1.15rem" }}>
        Neuer Lehrgang
      </h2>

      <div className={styles.spaced}>
        <label htmlFor="course-title">Titel</label>
        <input
          id="course-title"
          className={styles.field}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Grundkurs 2"
          maxLength={60}
        />
      </div>

      <div className={styles.spaced}>
        <label htmlFor="course-subtitle">Untertitel</label>
        <input
          id="course-subtitle"
          className={styles.field}
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="Klasse 4 · Obere und untere Reihe"
          maxLength={120}
        />
        <p className={styles.subtitle} style={{ marginTop: 6, marginBottom: 0 }}>
          Hier steht die Klassenstufe – nicht im Kürzel. Den Untertitel können Sie
          jederzeit ändern, das Kürzel nach der ersten Veröffentlichung nie wieder.
        </p>
      </div>

      <div className={styles.spaced}>
        <label htmlFor="course-slug">Kürzel</label>
        <input
          id="course-slug"
          className={styles.field}
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          placeholder="grundkurs-2"
          maxLength={64}
        />
        <p className={styles.subtitle} style={{ marginTop: 6, marginBottom: 0 }}>
          Kleinbuchstaben, Ziffern und Bindestriche. Das Kürzel steht im Dateipfad der
          veröffentlichten Fassungen und im Zwischenspeicher jedes iPads; nach der ersten
          Veröffentlichung lässt es sich nicht mehr ändern.
        </p>
        {slug !== "" && !slugOk && (
          <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
            {SLUG_SHAPE.test(slug)
              ? "Dieses Kürzel ist schon vergeben."
              : "Nur Kleinbuchstaben, Ziffern und Bindestriche."}
          </div>
        )}
      </div>

      <div className={styles.spaced}>
        <label htmlFor="course-requires">Baut auf</label>
        <select
          id="course-requires"
          className={styles.field}
          value={requires}
          onChange={(e) => setRequires(e.target.value)}
        >
          <option value="">– steht für sich –</option>
          {all.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <p className={styles.subtitle} style={{ marginTop: 6, marginBottom: 0 }}>
          Eine Klasse kann diesen Lehrgang erst führen, wenn sie den genannten führt.
          Kinder sehen davon nichts – wann eine Klasse so weit ist, entscheidet die
          Lehrkraft.
        </p>
      </div>

      <div className={styles.spaced}>
        <label htmlFor="course-copy">Beginnen mit</label>
        <select
          id="course-copy"
          className={styles.field}
          value={copyFrom}
          onChange={(e) => setCopyFrom(e.target.value)}
        >
          <option value="">Leerem Lehrgang (Tastatur und Finger vorbelegt)</option>
          {builtInAvailable && (
            <option value={BUILT_IN}>Eingebautem Lehrgang – Otto tippt los</option>
          )}
          {all
            .filter((c) => c.publishedVersion !== null)
            .map((c) => (
              <option key={c.id} value={c.slug}>
                Kopie von {c.title}
              </option>
            ))}
        </select>
        <p className={styles.subtitle} style={{ marginTop: 6, marginBottom: 0 }}>
          Eine Kopie übernimmt Tastatur, Finger, Sterne und Lektionen – vergibt aber alle
          Schlüssel neu. Ohne das teilten sich beide Lehrgänge den Fortschritt der Kinder.
          {builtInAvailable && (
            <>
              {" "}
              Der <strong>eingebaute Lehrgang</strong> ist der Lehrbuch-Stand aus der
              Anwendung selbst – gedacht für den ersten Lehrgang einer frischen Umgebung.
              Er steht nur zur Wahl, solange nichts veröffentlicht ist.
            </>
          )}
        </p>
      </div>

      {create.isError && (
        <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
          Der Lehrgang konnte nicht angelegt werden.
        </div>
      )}

      <div className={`${styles.row} ${styles.spaced}`}>
        <button
          type="submit"
          className={styles.button}
          disabled={!ready || create.isPending}
        >
          {create.isPending ? "Wird angelegt …" : "Anlegen und öffnen"}
        </button>
        <button
          type="button"
          className={styles.buttonQuiet}
          onClick={() => setOpen(false)}
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}
